import {Component, EventEmitter, Inject, OnDestroy, OnInit, Output} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from "@angular/material/dialog";
import {NgIf} from "@angular/common";
import {AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors} from "@angular/forms";

import {Subject, takeUntil} from "rxjs";
import {HttpErrorResponse} from "@angular/common/http";

import {EcuProducersService} from "../../service/ecu-producers.service";
import {CorrectEcuProducersModel} from "../../models/EcuBuildName-model";

@Component({
  selector: 'app-add-ecu-producer-dialog',
  standalone: true,
  imports: [
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    NgIf,
    ReactiveFormsModule
  ],
  templateUrl: './add-ecu-producer-dialog.component.html',
  styleUrls: ['./add-ecu-producer-dialog.component.css']
})
export class AddEcuProducerDialogComponent implements OnInit, OnDestroy {
  @Output() ecuProducerAdded = new EventEmitter<CorrectEcuProducersModel>();

  isSaving = false;
  saveError = '';
  private destroy$ = new Subject<void>();

  addNewEcuProducerForm = new FormGroup(
      {
        id: new FormControl(0),
        correctEcuProducer: new FormControl(null, [this.requiredTrimmed, this.ecuProducerValidator.bind(this)]),
        acceptedEcuProducer: new FormControl(null, this.requiredTrimmed),
      }
  )

  constructor(private ecuProducerService: EcuProducersService , public dialogRef: MatDialogRef<AddEcuProducerDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: any) {}

  ngOnInit() {
    this.addNewEcuProducerForm.patchValue({
      correctEcuProducer: this.data?.ecuProducerValue ?? null,
    });
    this.addNewEcuProducerForm.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.saveError = '');
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private requiredTrimmed(control: AbstractControl): ValidationErrors | null {
    return typeof control.value === 'string' && control.value.trim() ? null : {required: true};
  }

  ecuProducerValidator(control: AbstractControl): ValidationErrors | null {
    return this.ecuProducerService.checkDuplicateEcuProducer(control.value)
        ? {ecuProducerExists: true}
        : null;
  }

  canceledOption() {
    this.dialogRef.close({closed: false, ecuProducer: null});
  }

  saveNewRow() {
    if (this.isSaving || this.addNewEcuProducerForm.invalid) {
      this.addNewEcuProducerForm.markAllAsTouched();
      return;
    }

    this.saveError = '';
    const newRow: CorrectEcuProducersModel = {
      id: 0,
      correctEcuProducer: this.addNewEcuProducerForm.get('correctEcuProducer')?.value.trim(),
      acceptedEcuProducer: this.addNewEcuProducerForm.get('acceptedEcuProducer')?.value.trim(),
    };

    this.isSaving = true;
    this.ecuProducerService.addNewEcuProducerItem(newRow)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: savedProducer => {
            this.isSaving = false;
            this.ecuProducerAdded.emit(savedProducer);
            this.dialogRef.close({closed: true, ecuProducer: savedProducer});
          },
          error: (error: HttpErrorResponse) => {
            this.isSaving = false;
            const fieldErrors = error.error?.errors;
            this.saveError = error.error?.message ||
                (fieldErrors ? Object.values(fieldErrors).join(' ') : '') ||
                'Could not save the ECU Producer. Please try again.';
          }
        });
  }

}
